import { reactive } from "vue";
import { defineStore } from "pinia";
import api from "@/api";

// Single source of truth for a media/entity's comment list, keyed by (parentType,parentId).
//
// Why this exists (LSP-16440): the media lightbox renders TWO CommentBox instances
// (a desktop side-rail and a mobile full-screen overlay) gated by the 960px
// breakpoint. On an iPad rotation the visible instance is destroyed and the other
// revealed. When each instance owned its own `comments` array, a comment posted into
// the soon-to-be-destroyed instance was lost on rotation until a manual refresh.
//
// By holding the list in ONE store entry per typed parent, both instances read/write the
// same reactive list: a comment posted in either orientation is instantly visible in
// the other, with no per-instance refetch race and — because there is a single list
// that loadEntry REPLACES wholesale on fetch — no client-cache merge/duplicate risk.

export interface CommentEntry {
	comments: any[];
	commentCount: number;
	showCount: number;
	currentCount: number;
	currentPages: number;
	loaded: boolean;
	// Monotonic guard for last-request-wins. Bumped at the start of every loadEntry AND by
	// prependPosted, so a slow/older fetch that resolves out of order is discarded instead of
	// clobbering newer data (or wiping a just-posted comment). Mirrors useLatestRequest.
	loadSeq: number;
}

function createEntry(): CommentEntry {
	return { comments: [], commentCount: 0, showCount: 0, currentCount: 0, currentPages: 1, loaded: false, loadSeq: 0 };
}

export const useCommentsStore = defineStore("comments", () => {
	// reactive() (not ref of a Map) so per-parentId entries are deeply reactive and
	// every CommentBox bound to the same parentId re-renders on any mutation.
	const byParent = reactive<Record<string, CommentEntry>>({});

	function key(parentId: string, parentType?: string): string {
		return parentType ? `${parentType}:${parentId}` : parentId;
	}

	function entry(parentId: string, parentType?: string): CommentEntry {
		const entryKey = key(parentId, parentType);
		if (!byParent[entryKey]) {
			byParent[entryKey] = createEntry();
		}
		return byParent[entryKey];
	}

	// Full server reload for a parentId. Replaces the list wholesale (single source of
	// truth → idempotent by id → no duplicates). Existing comments stay visible while
	// the request is in flight, so a reload never blanks the panel.
	async function loadEntry(
		parentId: string,
		isAuthenticated: boolean,
		parentType?: string,
		publicProjectId?: string,
		publicShareId?: string
	): Promise<void> {
		const current = entry(parentId, parentType);
		// Claim this request. Only the latest claimant is allowed to write the result back,
		// so overlapping loads (two instances, mount + orientation reload, load-more + reload)
		// and stale fetches can't replace newer data out of order.
		const seq = ++current.loadSeq;
		const pageSize = 50 * current.currentPages;

		let comments: any = [];
		let commentsToShow: any = [];
		const visibleFilter = { $or: [{ deletedAt: { $eq: null } }, { replyCount: { $gt: 0 } }] };

		try {
			if (!isAuthenticated) {
				throw new Error("User is not authenticated.");
			}
			[comments, commentsToShow] = await Promise.all([
				api.getComments({ where: { parentId, ...(parentType ? { parentType } : {}) }, pageSize }),
				api.getComments({ where: { parentId, ...(parentType ? { parentType } : {}), ...visibleFilter }, pageSize: 1 })
			]);
		} catch (primaryError) {
			// The public fallback needs a full project-share scope. Authenticated render paths
			// (EntityComments -> CommentBox) never carry a shareId, so for them there is nothing to fall
			// back TO — the primary failure IS the failure. Logging it under "insecure" (as this used to)
			// described a request that was never attempted and sent anyone investigating a
			// "comments not loading" report looking at the public path instead of the authenticated one.
			if (!parentType || !publicProjectId || !publicShareId) {
				console.error("Error loading comments: ", primaryError, {
					parentId,
					parentType,
					isAuthenticated,
					missingPublicScope: [
						!parentType && "parentType",
						!publicProjectId && "publicProjectId",
						!publicShareId && "publicShareId"
					].filter(Boolean)
				});
			} else {
				try {
					[comments, commentsToShow] = await Promise.all([
						api.getCommentsInsecure({ where: { parentId, parentType }, pageSize }, publicProjectId, publicShareId),
						api.getCommentsInsecure(
							{
								where: { parentId, parentType, ...visibleFilter },
								pageSize: 1
							},
							publicProjectId,
							publicShareId
						)
					]);
				} catch (error) {
					console.error("Error loading comments insecure: ", error, { parentId, parentType, publicProjectId, publicShareId });
				}
			}
		}

		if (!comments || !comments.rows) {
			comments = { rows: [], count: 0 };
		}
		if (!commentsToShow) {
			commentsToShow = { count: 0 };
		}

		const target = entry(parentId, parentType);
		// A newer loadEntry — or a prependPosted (optimistic post) — superseded this request
		// while it was in flight. Discarding the stale result keeps the just-posted comment and
		// any newer/larger page set intact.
		if (seq !== target.loadSeq) {
			return;
		}
		target.currentCount = comments.rows.length;
		target.comments = comments.rows.filter((c: any) => c.deletedAt === null || c.replyCount > 0);
		target.commentCount = comments.count;
		target.showCount = commentsToShow.count;
		target.loaded = true;
	}

	// A just-posted comment (already persisted — carries a real server id). Prepend it
	// to the shared list so it is instantly visible to every bound CommentBox, in either
	// orientation. A later loadEntry replaces the list with the server result (which
	// includes this same row by id), so the prepend is never duplicated.
	function prependPosted(parentId: string, comment: any, parentType?: string): void {
		const target = entry(parentId, parentType);
		target.comments = [comment, ...target.comments];
		target.currentCount++;
		target.commentCount++;
		target.showCount++;
		// Invalidate any in-flight loadEntry so a fetch that started before this post (and may
		// not yet reflect it, e.g. read-replica lag) cannot resolve later and wipe the comment.
		target.loadSeq++;
	}

	function applyUpdate(parentId: string, comment: any, parentType?: string): void {
		const target = entry(parentId, parentType);
		target.comments = target.comments.map((c) => (c.id === comment.id ? comment : c));
	}

	function decrementShowCount(parentId: string, parentType?: string): void {
		entry(parentId, parentType).showCount--;
	}

	function incrementPages(parentId: string, parentType?: string): void {
		entry(parentId, parentType).currentPages++;
	}

	return { byParent, key, entry, loadEntry, prependPosted, applyUpdate, decrementShowCount, incrementPages };
});
